import type { PropertyFilterOption } from '@cloudscape-design/collection-hooks';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';

/**
 * Returns the full fixed vocabulary of Object Status labels usable in the My
 * Items ribbon filter editor — the union of every entity's status rating, not
 * just the statuses present in the currently-loaded items.
 *
 * Deriving options from the loaded data (as the ribbon did previously) meant
 * that once an admin saved a filter excluding e.g. Retired risks, "Retired" no
 * longer appeared in the dropdown because no Retired risk was returned — so
 * the filter could not be reopened or re-entered. Using a fixed vocabulary
 * lets admins add, remove, and re-add status tokens freely.
 *
 * Labels come from the same rating/taxonomy hooks the register and ribbon use
 * for display, so tenant taxonomy overrides are respected automatically.
 */
export const useGetStatusFilterOptions = (): PropertyFilterOption[] => {
  const actionRatings = useRating('action_status');
  const assessmentRatings = useRating('assessment_status');
  const activityRatings = useRating('assessment_activity_status');
  const issueRatings = useRating('issue_assessment_status');
  const { getOptions: getCommonOptions } = useCommonLookupLazy();

  return useMemo(() => {
    const riskStatuses = getCommonOptions('statuses');
    const labels = new Set<string>();

    [
      ...riskStatuses.map((o) => o.label),
      ...actionRatings.options.map((o) => o.label),
      ...assessmentRatings.options.map((o) => o.label),
      ...activityRatings.options.map((o) => o.label),
      ...issueRatings.options.map((o) => o.label),
    ]
      .filter((label): label is string => Boolean(label))
      .forEach((label) => labels.add(label));

    return Array.from(labels).map((label) => ({
      propertyKey: 'StatusLabelled',
      value: label,
      label,
    }));
  }, [
    getCommonOptions,
    actionRatings.options,
    assessmentRatings.options,
    activityRatings.options,
    issueRatings.options,
  ]);
};
